import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Refresh } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef, GridSortModel, GridValueGetterParams } from '@mui/x-data-grid-pro';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { formatUtcToLocal, writeXlsxFile } from '../helper/utils';
import {
  MiscReportTagMessageDialog,
  MiscSignatureAttachmentsDialog,
} from '../components/Elements/MiscNotificationDialogs';
import { MISC_INBOX_UPDATED_EVENT } from '../components/Icons/MiscInboxNotificationBell';
import { HoverUnselectedLightBlueBtnColor, PrimaryBlueColor } from '../helper/theme';
import { FaRegEye } from 'react-icons/fa';
import { MdOutlineRateReview } from 'react-icons/md';
import { RiArchiveLine, RiEye2Line, RiEyeCloseLine } from 'react-icons/ri';

/** Match CustomerInbox outlined filter/action buttons */
const outlinedToolbarBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  borderColor: '#e2e8f0',
  color: '#475569',
  '&:hover': { borderColor: '#2099d2', backgroundColor: '#2099d214', color: '#2099d2' },
};

/** Match CustomerInbox primary inbox actions */
const containedInboxBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  backgroundColor: '#2099d2',
  color: '#fff',
  '&:hover': { backgroundColor: '#176990', color: '#fff' },
};

/**
 * DataGrid look-and-feel aligned with Client Inbox (`CustomerInbox.tsx` main grid).
 * Pagination IconButtons scoped to `.MuiTablePagination-actions` so row “View” stays brand blue.
 */
const miscInboxDataGridSx = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  border: 'none',
  fontSize: '14px',
  color: '#475569',
  backgroundColor: '#ffffff',
  '& .MuiDataGridPro-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1.35,
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#64748b',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    borderTop: '2px solid #e2e8f0',
    minHeight: '52px !important',
    maxHeight: '52px !important',
  },
  '& .MuiDataGrid-columnHeader': { '&:focus, &:focus-within': { outline: 'none' } },
  '& .MuiDataGrid-row': {
    transition: 'background-color 0.15s ease',
    '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
    '&:hover': { backgroundColor: '#f1f5f9 !important' },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#334155',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    '&:focus': { outline: 'none' },
  },
  '& .MuiDataGrid-virtualScroller': { border: 'none' },
  '& .MuiDataGrid-main': { border: 'none' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    minHeight: 56,
  },
  '& .MuiTablePagination-root': { color: '#64748b', fontSize: '13px' },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { marginBottom: 0 },
  '& .MuiTablePagination-select': {
    fontSize: '13px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
  },
  '& .MuiTablePagination-toolbar': { alignItems: 'center' },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    color: '#64748b',
    '&:hover': { backgroundColor: '#f1f5f9', color: '#475569' },
  },
  '& .MuiCheckbox-root': {
    color: '#94a3b8',
    '&.Mui-checked': { color: '#2099d2' },
  },
  '& .MuiDataGrid-row.misc-inbox-row--unread': {
    backgroundColor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.12)' : 'rgba(25, 118, 210, 0.08)',
    fontWeight: 600,
  },
  '& .MuiDataGrid-row.misc-inbox-row--unread:nth-of-type(even)': {
    backgroundColor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.14)' : 'rgba(25, 118, 210, 0.1)',
  },
  '& .MuiDataGrid-row.misc-inbox-row--unread:hover': {
    backgroundColor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark'
        ? 'rgba(144, 202, 249, 0.2) !important'
        : 'rgba(25, 118, 210, 0.16) !important',
  },
};

const ALL_TEAM_VALUE = -1;

/** Server returns rows in this order (getMiscInbox.sql); prevents client sort on formatted date strings. */
const MISC_INBOX_SORT_MODEL: GridSortModel = [{ field: 'updatedDate', sort: 'desc' }];

const MISC_INBOX_CATEGORY = {
  tagNotification: 'Tag Notification',
  signatureRequest: 'Signature Request',
  tagExtraction: 'Tag Extraction',
} as const;

interface MiscInboxRow {
  inboxId: number;
  message: string;
  categoryName: string;
  partnerName: string;
  reportTagId: number | null;
  signatureRequestId: number | null;
  isRead: boolean;
  isArchived: boolean;
  createdDate: string;
  updatedDate?: string;
  filerUserId?: number;
  extractionRunId?: string | null;
}

function miscInboxActivityIso(row: MiscInboxRow): string | undefined {
  return row.updatedDate ?? row.createdDate;
}

interface TeamUserOption {
  userId: number;
  userName: string;
}

export const MiscNotifications: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = user?.token;
  const userId = user?.userId ?? 0;

  const [tagMessageOpen, setTagMessageOpen] = useState(false);
  const [tagMessageReportTagId, setTagMessageReportTagId] = useState(0);
  const [sigAttachmentsOpen, setSigAttachmentsOpen] = useState(false);
  const [sigAttachmentsRequestId, setSigAttachmentsRequestId] = useState(0);

  const roles = useMemo(
    () => (user?.userRoles ?? '').split(',').map((s) => s.trim()).filter(Boolean),
    [user?.userRoles]
  );
  const isSuperAdmin = roles.includes('Super Admin');
  const isMiscTeam = roles.some((r) =>
    ['Super Admin', 'MISC Filer', 'MISC State Reporter'].includes(r)
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<MiscInboxRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState<'New Messages' | 'Archive'>('New Messages');
  const [selectionModel, setSelectionModel] = useState<number[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<number>(0);

  const [teamUsers, setTeamUsers] = useState<TeamUserOption[]>([]);
  const [selectedScope, setSelectedScope] = useState<number>(() =>
    isSuperAdmin ? ALL_TEAM_VALUE : userId
  );

  useEffect(() => {
    setSelectedScope(isSuperAdmin ? ALL_TEAM_VALUE : userId);
  }, [userId, isSuperAdmin]);

  useEffect(() => {
    if (!token || !isMiscTeam || !userId) return;
    const selfLabel =
      `${user?.userFirstName ?? ''} ${user?.userLastName ?? ''}`.trim() || `User ${userId}`;

    if (!isSuperAdmin) {
      setTeamUsers([{ userId, userName: selfLabel }]);
      return;
    }

    apiClient('/Users/getAllUsers?isCustomerUser=false', {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((res) => {
        const list = (res?.data ?? []) as TeamUserOption[];
        const mapped = Array.isArray(list)
          ? list.map((u) => ({
              userId: u.userId,
              userName: u.userName ?? String(u.userId),
            }))
          : [];
        if (!mapped.some((u) => u.userId === userId)) {
          mapped.unshift({ userId, userName: selfLabel });
        }
        setTeamUsers(mapped);
      })
      .catch(() => setTeamUsers([{ userId, userName: selfLabel }]));
  }, [isSuperAdmin, isMiscTeam, token, userId, user?.userFirstName, user?.userLastName, user]);

  const getRecipientLabel = useCallback(
    (filerUserId?: number) => {
      if (filerUserId == null || !Number.isFinite(filerUserId)) return '—';
      const match = teamUsers.find((u) => u.userId === filerUserId);
      return match?.userName ?? `User ${filerUserId}`;
    },
    [teamUsers]
  );

  const fetchMiscInbox = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const isArchived = selectedTab === 'Archive';
    const viewAllTeam = isSuperAdmin && selectedScope === ALL_TEAM_VALUE;
    const body: Record<string, unknown> = {
      pageNumber: currentPage,
      pageSize,
      isArchived,
      clientInboxCategoryId: categoryFilter,
      field: 'updatedDate',
      sort: 'desc',
    };
    if (isSuperAdmin) {
      body.viewAllTeam = viewAllTeam;
      if (!viewAllTeam && selectedScope > 0) {
        body.forUserId = selectedScope;
      }
    }
    // Non–Super Admin MISC users are always scoped to JWT user on the server

    apiClient('/clientInbox/getAllMiscInbox', {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res?.data) return;
        const d = res.data as {
          clientInbox: MiscInboxRow[];
          totalCount: number;
        };
        setRows(Array.isArray(d.clientInbox) ? d.clientInbox : []);
        setTotalCount(Number.isFinite(d.totalCount) ? d.totalCount : 0);
      })
      .catch((e) => {
        console.error(e);
        setRows([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [token, currentPage, pageSize, selectedTab, categoryFilter, isSuperAdmin, selectedScope]);

  useEffect(() => {
    fetchMiscInbox();
  }, [fetchMiscInbox]);

  const handleClear = useCallback(() => {
    setCategoryFilter(0);
    setSelectedScope(isSuperAdmin ? ALL_TEAM_VALUE : userId);
    setCurrentPage(1);
  }, [userId, isSuperAdmin]);

  const markIds = (endpoint: string, ids: number[]) => {
    if (!token || ids.length === 0) return;
    apiClient(`${endpoint}?clientInboxIdList=${ids.join(',')}`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    })
      .then(() => {
        window.dispatchEvent(new Event(MISC_INBOX_UPDATED_EVENT));
        fetchMiscInbox();
        setSelectionModel([]);
      })
      .catch((e) => {
        console.error(e);
        Swal.fire({ title: 'Error', text: 'Action failed', icon: 'error', confirmButtonText: 'OK' });
      });
  };

  const openMessageCenterForRow = (row: MiscInboxRow) => {
    if (row.categoryName === MISC_INBOX_CATEGORY.tagExtraction && row.extractionRunId) {
      navigate(`/home/ReportTag?extractionId=${row.extractionRunId}`);
      return;
    }
    if (row.reportTagId) {
      setTagMessageReportTagId(row.reportTagId);
      setTagMessageOpen(true);
      return;
    }
    if (row.signatureRequestId) {
      setSigAttachmentsRequestId(row.signatureRequestId);
      setSigAttachmentsOpen(true);
    }
  };

  const openRow = (row: MiscInboxRow) => {
    if (!token) return;
    const afterRead = () => {
      window.dispatchEvent(new Event(MISC_INBOX_UPDATED_EVENT));
      setRows((prev) => prev.map((r) => (r.inboxId === row.inboxId ? { ...r, isRead: true } : r)));
      openMessageCenterForRow(row);
    };
    if (!row.isRead) {
      apiClient(`/clientInbox/markMiscInboxAsRead?clientInboxIdList=${row.inboxId}`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
      })
        .then(afterRead)
        .catch(() => openMessageCenterForRow(row));
      return;
    }
    openMessageCenterForRow(row);
  };

  const columns: GridColDef<MiscInboxRow>[] = [
    {
      field: 'message',
      headerName: 'Message',
      flex: 2.2,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => {
        const category = params.row.categoryName;
        let exclamationCount = 0;
        if (category === MISC_INBOX_CATEGORY.tagNotification) exclamationCount = 1;
        else if (category === MISC_INBOX_CATEGORY.signatureRequest) exclamationCount = 2;
        const text = params.value ?? '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            {exclamationCount > 0 && (
              <Box component="span" sx={{ color: 'error.main', fontWeight: 700, flexShrink: 0 }}>
                {'!'.repeat(exclamationCount)}
              </Box>
            )}
            {params.row.isRead ? <span>{text}</span> : <strong>{text}</strong>}
          </Box>
        );
      },
    },
    {
      field: 'categoryName',
      headerName: 'Category',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const cat = params.row.categoryName ?? '';
        return params.row.isRead ? cat : <strong>{cat}</strong>;
      },
    },
    {
      field: 'partnerName',
      headerName: 'Client',
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        const name = params.row.partnerName ?? '';
        return params.row.isRead ? <span>{name}</span> : <strong>{name}</strong>;
      },
    },
    {
      field: 'recipient',
      headerName: 'Recipient',
      flex: 0.75,
      minWidth: 140,
      sortable: false,
      valueGetter: (params: GridValueGetterParams<MiscInboxRow>) =>
        getRecipientLabel(params.row.filerUserId),
      renderCell: (params) => {
        const label = getRecipientLabel(params.row.filerUserId);
        return params.row.isRead ? <span>{label}</span> : <strong>{label}</strong>;
      },
    },
    {
      field: 'updatedDate',
      headerName: 'Last updated',
      width: 180,
      sortable: false,
      valueGetter: (params: GridValueGetterParams<MiscInboxRow>) => {
        const iso = miscInboxActivityIso(params.row);
        return iso ? formatUtcToLocal(iso, 'MM/DD/YYYY hh:mm A') : '';
      },
      renderCell: (params) => {
        const iso = miscInboxActivityIso(params.row);
        const label = iso ? formatUtcToLocal(iso, 'MM/DD/YYYY hh:mm A') : '';
        return params.row.isRead ? <span>{label}</span> : <strong>{label}</strong>;
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 88,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isExtraction = params.row.categoryName === MISC_INBOX_CATEGORY.tagExtraction;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Tooltip title={isExtraction ? 'Review' : 'View'} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openRow(params.row);
                }}
                sx={{
                  color: PrimaryBlueColor,
                  backgroundColor: HoverUnselectedLightBlueBtnColor,
                  '&:hover': {
                    backgroundColor: 'rgba(32, 153, 210, 0.18)',
                    color: PrimaryBlueColor,
                  },
                }}
                aria-label={isExtraction ? 'Review extraction' : 'View notification'}
              >
                {isExtraction ? <MdOutlineRateReview /> : <FaRegEye />}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const selectedIds = selectionModel.filter((id) => id != null);

  const exportMiscInboxToExcel = () => {
    const buildExportRows = (data: MiscInboxRow[]): (string | number)[][] => {
      const headers = ['Message', 'Category', 'Client', 'Recipient', 'Last updated', 'Read', 'Archived'];
      const dataRows = data.map((row) => {
        const activityIso = miscInboxActivityIso(row);
        return [
        row.message ?? '',
        row.categoryName ?? '',
        row.partnerName ?? '',
        getRecipientLabel(row.filerUserId),
        activityIso ? formatUtcToLocal(activityIso, 'MM/DD/YYYY hh:mm A') : '',
        row.isRead ? 'Yes' : 'No',
        row.isArchived ? 'Yes' : 'No',
        ];
      });
      return [headers, ...dataRows];
    };

    const doExport = (data: MiscInboxRow[]) => {
      if (!data || data.length === 0) {
        Swal.fire({
          title: 'No data',
          text: 'No notifications to export.',
          icon: 'info',
          confirmButtonText: 'OK',
        });
        return;
      }
      const exportData = buildExportRows(data);
      const filename = `MiscNotifications_${formatUtcToLocal(new Date().toISOString(), 'YYYY-MM-DD_HHmm')}.xlsx`;
      writeXlsxFile(exportData, filename);
    };

    if (!token) return;

    const selectedSet = new Set(selectedIds);
    if (selectedIds.length > 0) {
      doExport(rows.filter((r) => selectedSet.has(r.inboxId)));
      return;
    }

    setLoading(true);
    const isArchived = selectedTab === 'Archive';
    const viewAllTeam = isSuperAdmin && selectedScope === ALL_TEAM_VALUE;
    const body: Record<string, unknown> = {
      pageNumber: 1,
      pageSize: Math.max(totalCount || 1, 1),
      isArchived,
      clientInboxCategoryId: categoryFilter,
      field: 'updatedDate',
      sort: 'desc',
    };
    if (isSuperAdmin) {
      body.viewAllTeam = viewAllTeam;
      if (!viewAllTeam && selectedScope > 0) {
        body.forUserId = selectedScope;
      }
    }

    apiClient('/clientInbox/getAllMiscInbox', {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(body),
    })
      .then((res) => {
        const list = res?.data?.clientInbox as MiscInboxRow[] | undefined;
        if (!Array.isArray(list)) {
          doExport(rows);
          return;
        }
        doExport(list);
      })
      .catch(() => {
        doExport(rows);
      })
      .finally(() => setLoading(false));
  };

  const selectFieldSx = {
    marginTop: '-1px !important',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
    '& .MuiInputBase-root': { marginTop: '-3px !important' },
    '& .MuiInputBase-input': { padding: '7px 14px !important' },
  } as const;

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Top bar: title | actions — match CustomerInbox */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#fafbfc',
          minHeight: 56,
          mb: 1,
          mx: { xs: -2, sm: 0 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', mr: 'auto', minWidth: 0 }}>
          <Typography component="h2" sx={{ m: 0, fontWeight: 600, fontSize: '1.25rem', color: '#0f172a' }}>
            My Notifications
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: '#64748b', mt: 0.25 }}>
            Tag and signature activity when clients reply or upload documents.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            size="medium"
            startIcon={<RiEye2Line style={{ fontSize: 16 }} />}
            disabled={selectedIds.length === 0}
            sx={containedInboxBtnSx}
            onClick={() => markIds('/clientInbox/markMiscInboxAsRead', selectedIds)}
          >
            Mark as Read
          </Button>
          <Button
            variant="contained"
            size="medium"
            startIcon={<RiEyeCloseLine style={{ fontSize: 16 }} />}
            disabled={selectedIds.length === 0}
            sx={containedInboxBtnSx}
            onClick={() => markIds('/clientInbox/markMiscInboxAsUnread', selectedIds)}
          >
            Mark as Unread
          </Button>
          <Button
            variant="contained"
            size="medium"
            startIcon={<RiArchiveLine style={{ fontSize: 16 }} />}
            disabled={selectedIds.length === 0}
            sx={containedInboxBtnSx}
            onClick={() =>
              markIds(
                selectedTab === 'Archive'
                  ? '/clientInbox/markMiscInboxAsUnarchived'
                  : '/clientInbox/markMiscInboxAsArchived',
                selectedIds
              )
            }
          >
            {selectedTab === 'Archive' ? 'Move to New Messages' : 'Move to Archive'}
          </Button>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Refresh />}
            onClick={() => fetchMiscInbox()}
            sx={outlinedToolbarBtnSx}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="medium"
            onClick={exportMiscInboxToExcel}
            disabled={loading}
            sx={outlinedToolbarBtnSx}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          py: 2,
          px: { xs: 0, sm: 0 },
          mb: 1,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {isMiscTeam && teamUsers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="misc-user-scope">Users</InputLabel>
              <Select
                labelId="misc-user-scope"
                label="Users"
                value={isSuperAdmin ? selectedScope : userId}
                onChange={(e) => {
                  setSelectedScope(Number(e.target.value));
                  setCurrentPage(1);
                }}
                sx={selectFieldSx}
              >
                {isSuperAdmin && <MenuItem value={ALL_TEAM_VALUE}>All Users</MenuItem>}
                {teamUsers.map((u) => (
                  <MenuItem key={u.userId} value={u.userId}>
                    {u.userName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="misc-cat-filter">Category</InputLabel>
            <Select
              labelId="misc-cat-filter"
              label="Category"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(Number(e.target.value));
                setCurrentPage(1);
              }}
              sx={selectFieldSx}
            >
              <MenuItem value={0}>Category</MenuItem>
              <MenuItem value={1}>{MISC_INBOX_CATEGORY.tagNotification}</MenuItem>
              <MenuItem value={2}>{MISC_INBOX_CATEGORY.signatureRequest}</MenuItem>
              <MenuItem value={5}>{MISC_INBOX_CATEGORY.tagExtraction}</MenuItem>
            </Select>
          </FormControl>

          <Button variant="outlined" size="medium" onClick={handleClear} sx={outlinedToolbarBtnSx}>
            Clear
          </Button>
        </Box>
      </Box>

      <Paper elevation={1}>
        <Tabs
          value={selectedTab}
          onChange={(_, v) => {
            setSelectedTab(v);
            setCurrentPage(1);
          }}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 1,
            minHeight: 48,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
            '& .Mui-selected': { color: '#2099d2' },
            '& .MuiTabs-indicator': {
              backgroundColor: '#2099d2',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label="New Messages" value="New Messages" />
          <Tab label="Archive" value="Archive" />
        </Tabs>
        {loading && <LinearProgress />}
        <Box sx={{ height: 560, width: '100%' }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            getRowId={(r) => r.inboxId}
            getRowClassName={(params) => (!params.row.isRead ? 'misc-inbox-row--unread' : '')}
            loading={loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            sortModel={MISC_INBOX_SORT_MODEL}
            rowCount={totalCount}
            page={currentPage - 1}
            pageSize={pageSize}
            onPageChange={(newPage) => setCurrentPage(newPage + 1)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[25, 50, 100, 250, 500, 1000]}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectionModel}
            onSelectionModelChange={(m) => setSelectionModel(m as number[])}
            components={{
              NoRowsOverlay: () => (
                <Typography variant="body1" sx={{ textAlign: 'center', padding: '20px' }}>
                  No notifications found
                </Typography>
              ),
            }}
            sx={miscInboxDataGridSx}
          />
        </Box>
      </Paper>

      <MiscReportTagMessageDialog
        open={tagMessageOpen}
        reportTagId={tagMessageReportTagId}
        token={token}
        onClose={() => {
          setTagMessageOpen(false);
          setTagMessageReportTagId(0);
        }}
        onRefresh={fetchMiscInbox}
      />
      <MiscSignatureAttachmentsDialog
        open={sigAttachmentsOpen}
        signatureRequestId={sigAttachmentsRequestId}
        token={token}
        onClose={() => {
          setSigAttachmentsOpen(false);
          setSigAttachmentsRequestId(0);
        }}
      />
    </Box>
  );
};

export default MiscNotifications;
